import type { RuntimeAPI } from "../dotnet";
import type { AxCursorType } from "./types";
import { outerObjectChanged } from "../bridge/bridge";
import { clientUpdateRequested } from "../bridge/clientUpdate";
import {
  backupRestored,
  connectedClientFollowingChanged,
  connectedClientsChanged,
  followingEnded,
  mouseStatusChanged,
  restoreBackupStarted,
} from "../bridge/collaboration";
import {
  documentFinishedLoading,
  documentStartedLoading,
} from "../bridge/document";
import {
  bridgeReady,
  copyObjectLink,
  currentEditorModeChanged,
  gradientModeGeometryChanged,
  isMouseOverGradientModeHotZoneChanged,
  scrollZoomChanged,
  shapeManagerPointsUpdated,
} from "../bridge/editor";
import { setMoving } from "../bridge/editorState";
import { enabledChanged } from "../bridge/globalCommands";
import { maintenanceModeChanged } from "../bridge/maintenanceMode";
import { loadingFailed } from "../bridge/projectState";
import { selectionChanged } from "../bridge/selection";
import { addToLibraryRequested, bindAction, bindDestroy, show as showContextMenu } from "../context-menu/state";
import { getEmojiImageIntoBuffer } from "../emojis/utils";
import { create, openCardLinkTriggered } from "../platform/card";
import { getClientSetting, setClientSetting } from "../platform/clientSettings";
import {
  clearData,
  dataTransferTryReadAsHtmlBlob,
  dataTransferTryReadAttributedString,
  dataTransferTryReadFiles,
  dataTransferTryReadPlainText,
  deleteFile,
  getImageData,
  getMimeTypes,
  getTextData,
  isNavigatorClipboardApiSupported,
  isNavigatorClipboardWriteSupported,
  readFileIntoBuffer,
  tryReadAsHtmlBlobFromClipboardApi,
  writeAxvg,
  writeHtmlBlob,
  writeHtmlBlobToDataTransfer,
  writePlainText,
} from "../platform/clipboard";
import { clickOnDiagram } from "../platform/comments";
import {
  requestShapeTriggered,
} from "../platform/connector";
import { diagramPlayerFrameChanged, diagramPlayersChanged } from "../platform/diagramPlayer";
import { editApplied, editInProgress, outlineEditApplied } from "../platform/edit";
import { toDiagramEditActionType } from "../platform/edit.types";
import { cueOpenFile, deleteDialogFile, fileTransferTryReadFiles, readDialogFileIntoBuffer } from "../platform/fileDialog";
import { loadBundledFont, loadFont } from "../platform/fontLoading";
import { editorHubConnectionStateChanged } from "../platform/healthChecks";
import { focusCanvas, invalidateCanvas, setCursor, toastMessage } from "../platform/hosting";
import { convertSvg } from "../platform/imageProcessing";
import { getOS, getSystemInformation } from "../platform/information";
import {
  activeLinksChanged,
  editLinkTriggered,
  fetchMetadata,
  getUserMetadataInfo,
  getWellKnownServiceInfo,
  iframeWidgetAdded,
  iframeWidgetDeleted,
  iframeWidgetEdited,
  requestUrl,
} from "../platform/smartLink";
import {
  applyStyle,
  canRedo,
  canUndo,
  clearUndoStack,
  copy,
  createEditor,
  cut,
  deleteText,
  disposeEditor,
  getAcceptsReturn,
  getAllowCutCopy,
  getAllowDelete,
  getAllowPaste,
  getAllowSelectAll,
  getAllowUndoRedo,
  getAttributedString,
  getAutoFitWidth,
  getIsEmpty,
  getIsReadOnly,
  getJsonValue,
  getRect,
  getSelectionCanBecomeLink,
  getSelectionIsEmpty,
  getSelectionRange,
  getSelectionStyle,
  getStripsNewlines,
  getText,
  getVerticalAlignment,
  getVisible,
  paste,
  redo,
  selectAll,
  setAcceptsReturn,
  setAllowUndoRedo,
  setAttributedString,
  setAutoFitWidth,
  setBaseStyle,
  setFocus,
  setJsonValue,
  setRect,
  setStripsNewlines,
  setText,
  setVerticalAlignment,
  setVisible,
  undo,
} from "../platform/textEditor";
import { hide, show } from "../tooltip/state";
import { setupDotnetExports } from "./imports";

const JS_IMPORTS_MODULE_NAME = "Axure.Platform.Web.Interop.JSImports";

function setupImports(runtime: RuntimeAPI) {
  if (!runtime) {
    throw new Error("Couldn't locate dotnet runtime. Make sure that it is imported.");
  }

  const imports = {
    setupDotnetExports,
    platform: {
      getOS,
      getSystemInformation,
      getClientSetting,
      setClientSetting,
    },
    hosting: {
      focusCanvas,
      invalidateCanvas,
      setCursor: (cursor: AxCursorType, cursorName: string | null) => setCursor({ cursor, cursorName: cursorName ?? undefined }),
      toastMessage: (title: string, description: string) => toastMessage({ title, description }),
    },
    healthCheck: {
      editorHubConnectionStateChanged,
    },
    clipboard: {
      isNavigatorClipboardApiSupported,
      isNavigatorClipboardWriteSupported,
      getMimeTypes,
      getTextData,
      getImageData,
      dataTransferTryReadAsHtmlBlob,
      tryReadAsHtmlBlobFromClipboardApi,
      dataTransferTryReadPlainText,
      dataTransferTryReadAttributedString,
      dataTransferTryReadFiles,
      writeHtmlBlob,
      writeHtmlBlobToDataTransfer,
      writePlainText,
      writeAxvg,
      readFileIntoBuffer,
      clearData,
      deleteFile,
    },
    comments: {
      clickOnDiagram,
    },
    tooltip: {
      show,
      hide,
    },
    card: {
      create,
      openCardLinkTriggered,
    },
    diagramPlayer: {
      diagramPlayersChanged,
      diagramPlayerFrameChanged,
    },
    smartLink: {
      activeLinksChanged,
      requestUrl,
      editLinkTriggered,
      fetchMetadata,
      getUserMetadataInfo,
      getWellKnownServiceInfo,
      iframeWidgetAdded,
      iframeWidgetDeleted,
      iframeWidgetEdited,
    },
    connector: {
      requestShapeTriggered,
    },
    fileDialog: {
      cueOpenFile,
      deleteDialogFile,
      fileTransferTryReadFiles,
      readDialogFileIntoBuffer,
    },
    fontLoading: {
      loadFont,
      loadBundledFont,
    },
    imageProcessing: {
      convertSvg,
      getEmojiImageIntoBuffer,
    },
    edit: {
      editInProgress: (isExternal: boolean, objectIds: string[], action: number, undoKey: string) => editInProgress({ isExternal, objectIds, action: toDiagramEditActionType(action), undoKey, isRollback: false }),
      editApplied: (isExternal: boolean, objectIds: string[], action: number, undoKey: string, isRollback: boolean) => editApplied({ isExternal, objectIds, action: toDiagramEditActionType(action), undoKey, isRollback }),
      outlineEditApplied: () => outlineEditApplied(),
    },
    editor: {
      copyObjectLink,
      selectionChanged: (objectIds: string[]) => selectionChanged({ objectIds }),
      currentEditorModeChanged,
      gradientModeGeometryChanged,
      isMouseOverGradientModeHotZoneChanged,
      bridgeReady,
      scrollZoomChanged,
      shapeManagerPointsUpdated,
    },
    globalCommands: {
      enabledChanged,
    },
    bridge: {
      outerObjectChanged,
    },
    editorState: {
      setMoving,
    },
    collaboration: {
      connectedClientsChanged,
      mouseStatusChanged,
      followingEnded,
      restoreBackupStarted,
      backupRestored,
      connectedClientFollowingChanged,
    },
    document: {
      documentFinishedLoading,
      documentStartedLoading,
    },
    textEditor: {
      createEditor,
      disposeEditor,
      getRect,
      setRect,
      getVisible,
      setVisible,
      getAllowUndoRedo,
      setAllowUndoRedo,
      canUndo,
      canRedo,
      getAllowCutCopy,
      getAllowPaste,
      getAllowDelete,
      getAllowSelectAll,
      getIsEmpty,
      getSelectionIsEmpty,
      getSelectionCanBecomeLink,
      getIsReadOnly,
      getAcceptsReturn,
      setAcceptsReturn,
      getStripsNewlines,
      setStripsNewlines,
      getAutoFitWidth,
      setAutoFitWidth,
      clearUndoStack,
      copy,
      cut,
      deleteText,
      setFocus,
      paste,
      undo,
      redo,
      selectAll,
      setText,
      getText,
      setAttributedString,
      setBaseStyle,
      getAttributedString,
      setJsonValue,
      getJsonValue,
      applyStyle,
      getSelectionStyle,
      getSelectionRange,
      setVerticalAlignment,
      getVerticalAlignment,
    },
    contextMenu: {
      bindAction,
      bindDestroy,
      show: showContextMenu,
      addToLibraryRequested,
    },
    projectState: {
      loadingFailed,
    },
    maintenanceMode: {
      maintenanceModeChanged,
    },
    clientUpdate: {
      clientUpdateRequested,
    },
  };

  runtime.setModuleImports(JS_IMPORTS_MODULE_NAME, imports);
}

export {
  setupImports,
};
